import React, { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useGetInclusionsCategories } from '~/hooks/apis/inclusions/useGetInclusionsCategories';
import { useGetPackageInclusionsByOffer } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import type { PackageInclusionRecord as OfferPackageInclusionRecord } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';

import { getPackageName, getPackagePersistId } from './OfferPackages/utils';
import { type PackageInclusionRecord, getInclusionCategory, getInclusionName } from './utils';

interface MasterInclusion {
  id: string;
  assignedPackageIds: Array<string>;
  category: string;
  highlighted: boolean;
  inclusion: string;
  luxPlusOnly: boolean;
}

interface Props {
  offerId?: string;
  packages?: Array<App.Package>;
}

interface PackageOption {
  id: string;
  name: string;
}

type OfferPackageInclusionWithDisplayFields = OfferPackageInclusionRecord &
  PackageInclusionRecord & {
    inclusion: OfferPackageInclusionRecord['inclusion'] & NonNullable<PackageInclusionRecord['inclusion']>;
  };

const FALLBACK_CATEGORY_OPTION = 'Other';
const TABLE_GRID_COLUMNS = '40px minmax(150px, 180px) minmax(260px, 1fr) 110px 130px minmax(200px, 240px) 40px';
const PACKAGE_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 360,
      mt: 0.5,
      '& .MuiMenuItem-root': {
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        whiteSpace: 'normal',
      },
      '& .MuiMenuItem-root.Mui-selected': {
        bgcolor: 'background.paper',
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        bgcolor: 'action.hover',
      },
      '& .MuiListItemText-primary': {
        whiteSpace: 'normal',
      },
    },
  },
};

function createInclusion(packageOptions: Array<PackageOption>): MasterInclusion {
  return {
    id: `local-inclusion-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    assignedPackageIds: packageOptions.map((option) => option.id),
    category: 'Other',
    highlighted: false,
    inclusion: '',
    luxPlusOnly: false,
  };
}

function getPackageOptions(packages?: Array<App.Package>): Array<PackageOption> {
  return (
    packages?.map((offerPackage, index) => ({
      id: getPackagePersistId(offerPackage) ?? `package-${index}`,
      name: getPackageName(offerPackage),
    })) ?? []
  );
}

function getInclusionKey(inclusion: OfferPackageInclusionWithDisplayFields) {
  return getInclusionName(inclusion).trim().toLowerCase();
}

function buildInclusionsFromOffer(
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<MasterInclusion> {
  if (!packageInclusions) {
    return [];
  }

  const inclusionsById = new Map<string, MasterInclusion>();

  Object.entries(packageInclusions).forEach(([packageId, inclusions]) => {
    inclusions.forEach((rawInclusion) => {
      // Package inclusion payloads can include extra display fields; missing values intentionally fall back to off.
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionName = getInclusionName(inclusion);

      if (!inclusionName) {
        return;
      }

      const inclusionKey = getInclusionKey(inclusion);
      const existingInclusion = inclusionsById.get(inclusionKey);

      if (existingInclusion) {
        if (!existingInclusion.assignedPackageIds.includes(packageId)) {
          existingInclusion.assignedPackageIds.push(packageId);
        }

        return;
      }

      inclusionsById.set(inclusionKey, {
        id: inclusionKey,
        assignedPackageIds: [packageId],
        category: getInclusionCategory(inclusion),
        highlighted: inclusion.style === 'highlighted',
        inclusion: inclusionName,
        luxPlusOnly: !!inclusion.lux_plus_tier,
      });
    });
  });

  return Array.from(inclusionsById.values());
}

function getAssignedPackagesLabel(selectedPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  if (packageOptions.length === 0) {
    return 'No packages available';
  }

  if (selectedPackageIds.length === 0) {
    return 'No packages';
  }

  if (selectedPackageIds.length === packageOptions.length) {
    return 'All packages';
  }

  if (selectedPackageIds.length === 1) {
    return packageOptions.find((option) => option.id === selectedPackageIds[0])?.name ?? '1 package';
  }

  return `${selectedPackageIds.length} of ${packageOptions.length} packages`;
}

export default function Inclusions({ offerId, packages }: Props) {
  const { data: packageInclusions, isLoading: isLoadingInclusions } = useGetPackageInclusionsByOffer({ offerId });
  const { data: inclusionCategories = [] } = useGetInclusionsCategories();
  const packageOptions = useMemo(() => getPackageOptions(packages), [packages]);
  const offerInclusions = useMemo(() => buildInclusionsFromOffer(packageInclusions), [packageInclusions]);
  const [inclusions, setInclusions] = useState<Array<MasterInclusion>>([]);
  const categoryOptions = useMemo(() => {
    const fetchedCategories = inclusionCategories.map(({ category }) => category);
    const offerCategories = inclusions.map((inclusion) => inclusion.category).filter((category) => !!category);

    return Array.from(new Set(fetchedCategories.concat(offerCategories, FALLBACK_CATEGORY_OPTION)));
  }, [inclusionCategories, inclusions]);

  useEffect(() => {
    setInclusions(offerInclusions);
  }, [offerInclusions]);

  const updateInclusion = (id: string, nextValues: Partial<MasterInclusion>) => {
    setInclusions((currentInclusions) =>
      currentInclusions.map((inclusion) => (inclusion.id === id ? { ...inclusion, ...nextValues } : inclusion)),
    );
  };

  const deleteInclusion = (id: string) => {
    setInclusions((currentInclusions) => currentInclusions.filter((inclusion) => inclusion.id !== id));
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Inclusions</Typography>
          <Typography variant="body1" color="text.secondary">
            Master list of inclusions across all offer packages. Sets the default ordering; each package's own list is
            kept for manual overrides.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            setInclusions((currentInclusions) => currentInclusions.concat(createInclusion(packageOptions)))
          }
          sx={{ flexShrink: 0 }}
        >
          Add inclusion
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drag <DragIndicatorIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom' }} /> to reorder. Inclusions
        follow this master order &mdash; but any inclusion unique to a single package appears first within that package.
      </Typography>

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'grey.50',
              display: { xs: 'none', lg: 'grid' },
              gap: 2,
              gridTemplateColumns: TABLE_GRID_COLUMNS,
              px: 2,
              py: 1.5,
            }}
          >
            <Box />
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Category
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Inclusion
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Highlight
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              LuxPlus only
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Assigned packages
            </Typography>
            <Box />
          </Box>

          {isLoadingInclusions && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading inclusions...
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions && inclusions.length === 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No inclusions are currently available for this offer.
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions &&
            inclusions.map((inclusion) => (
              <Box
                key={inclusion.id}
                sx={{
                  alignItems: 'center',
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', lg: TABLE_GRID_COLUMNS },
                  px: 2,
                  py: 2,
                  '& .MuiInputBase-root': {
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Reorder ${inclusion.inclusion || 'inclusion'}`}
                  sx={{ color: 'text.disabled', cursor: 'grab' }}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>

                <FormControl size="small" fullWidth>
                  <Select
                    value={inclusion.category}
                    onChange={(event) => updateInclusion(inclusion.id, { category: event.target.value })}
                  >
                    {categoryOptions.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  value={inclusion.inclusion}
                  onChange={(event) => updateInclusion(inclusion.id, { inclusion: event.target.value })}
                  size="small"
                  fullWidth
                />

                <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
                  <Switch
                    size="small"
                    checked={inclusion.highlighted}
                    onChange={(event) =>
                      updateInclusion(inclusion.id, {
                        highlighted: event.target.checked,
                        luxPlusOnly: event.target.checked ? false : inclusion.luxPlusOnly,
                      })
                    }
                    disabled={inclusion.luxPlusOnly}
                  />
                </Stack>

                <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
                  <Switch
                    size="small"
                    checked={inclusion.luxPlusOnly}
                    onChange={(event) =>
                      updateInclusion(inclusion.id, {
                        highlighted: event.target.checked ? false : inclusion.highlighted,
                        luxPlusOnly: event.target.checked,
                      })
                    }
                    disabled={inclusion.highlighted}
                  />
                </Stack>

                <FormControl size="small" fullWidth>
                  <Select
                    multiple
                    value={inclusion.assignedPackageIds}
                    displayEmpty
                    renderValue={(selected) => getAssignedPackagesLabel(selected, packageOptions)}
                    MenuProps={PACKAGE_SELECT_MENU_PROPS}
                    onChange={(event) => {
                      const value = event.target.value;
                      const assignedPackageIds = typeof value === 'string' ? value.split(',') : value;

                      updateInclusion(inclusion.id, { assignedPackageIds });
                    }}
                  >
                    {packageOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        <Checkbox
                          size="small"
                          checked={inclusion.assignedPackageIds.includes(option.id)}
                          sx={{ p: 0.25 }}
                        />
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton color="error" aria-label="Delete inclusion" onClick={() => deleteInclusion(inclusion.id)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
        </Paper>
      </Box>
    </Box>
  );
}
